import React, { useState } from 'react';
import {
  MovieProjectDetail,
  SceneInput,
  MOVIE_VIDEO_MODELS,
  MovieVideoModel,
} from '../../services/api/movieService';
import { TaskySelect } from '../tasky';
import VideoModelSelectModal from '../video-generation/VideoModelSelectModal';
import { getVideoModelSpec } from '../video-generation/videoModelCatalog';
import {
  GenerationInputMode,
  GenerationInputModeTabs,
  ProductGenerationPanel,
  ProductSelectionData,
  ProductSelectionModal,
} from '../image-generation';
import { buildProductGenerationPrompt } from '../../utils/productGenerationPrompt';
import { useSetAdmanPageContext } from '../adman/useAdmanPageContext';
import { Sparkles, Plus, Trash2, ArrowUp, ArrowDown, Save, Loader2 } from 'lucide-react';

/**
 * ステップ①: 台本・構成。
 *
 * コンセプト・スタイル・尺などのプロジェクト設定と、
 * AIによるシーン構成の生成 + 手動編集（追加/削除/並べ替え）を行う。
 * ここで確定したシーンが ②絵コンテ ③クリップ の作業単位になる。
 */

interface SceneDraft extends SceneInput {
  /** React key 用（新規行は一時IDを振る） */
  key: string;
}

interface ScriptStepProps {
  project: MovieProjectDetail;
  scenes: SceneDraft[];
  hasGeneratedAssets: boolean;
  isSavingMeta: boolean;
  isGeneratingScript: boolean;
  isSavingScenes: boolean;
  onMetaChange: (patch: Partial<MovieProjectDetail>) => void;
  onSaveMeta: () => Promise<void>;
  onGenerateScript: () => Promise<void>;
  onScenesChange: (scenes: SceneDraft[]) => void;
  onSaveScenes: () => Promise<void>;
}

export type { SceneDraft };

const DURATION_OPTIONS = [5, 6, 8, 12];

const ScriptStep: React.FC<ScriptStepProps> = ({
  project,
  scenes,
  hasGeneratedAssets,
  isSavingMeta,
  isGeneratingScript,
  isSavingScenes,
  onMetaChange,
  onSaveMeta,
  onGenerateScript,
  onScenesChange,
  onSaveScenes,
}) => {
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const modelSpec = getVideoModelSpec(project.video_model);
  const [inputMode, setInputMode] = useState<GenerationInputMode>(
    project.concept?.trim() ? 'freetext' : 'product'
  );
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);

  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    setShowProductSelectionModal(false);
    onMetaChange({
      concept: `選択した商品のプロモーションムービーを制作してください。\n${buildProductGenerationPrompt(data)}`,
    });
  };

  const handleAdditionalInstructionsChange = (value: string) => {
    setAdditionalInstructions(value);
    if (selectedProductData) {
      onMetaChange({
        concept: `選択した商品のプロモーションムービーを制作してください。\n${buildProductGenerationPrompt(selectedProductData, value)}`,
      });
    }
  };

  const moveScene = (index: number, direction: -1 | 1) => {
    const next = [...scenes];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onScenesChange(next);
  };

  const updateScene = (index: number, patch: Partial<SceneDraft>) => {
    const next = [...scenes];
    next[index] = { ...next[index], ...patch };
    onScenesChange(next);
  };

  const addScene = () => {
    if (scenes.length >= 10) return;
    onScenesChange([
      ...scenes,
      { key: `new-${Date.now()}`, summary: '', duration_sec: 8, storyboard_prompt: '', clip_prompt: '' },
    ]);
  };

  const removeScene = (index: number) => {
    onScenesChange(scenes.filter((_, i) => i !== index));
  };

  const handleGenerateClick = () => {
    if (scenes.length > 0 && !confirmRegenerate) {
      setConfirmRegenerate(true);
      return;
    }
    setConfirmRegenerate(false);
    onGenerateScript();
  };

  const totalDuration = scenes.reduce((sum, s) => sum + (s.duration_sec || 0), 0);

  return (
    <div className="space-y-6">
      {/* プロジェクト設定 */}
      <div className="bg-gray-50 rounded-xl p-4 border border-warm-border">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">プロジェクト設定</h3>

        <div className="mb-3">
          <GenerationInputModeTabs
            value={inputMode}
            onChange={setInputMode}
            disabled={isGeneratingScript || isSavingMeta}
          />
        </div>

        {inputMode === 'product' ? (
          <div className="mb-3">
            <ProductGenerationPanel
              selectedProductData={selectedProductData}
              additionalInstructions={additionalInstructions}
              onAdditionalInstructionsChange={handleAdditionalInstructionsChange}
              onSelectProduct={() => setShowProductSelectionModal(true)}
              disabled={isGeneratingScript || isSavingMeta}
            />
          </div>
        ) : (
          <>
            <label className="block text-xs text-gray-600 mb-1">
              コンセプト <span className="text-red-500">*</span>（どんな動画を作りたいか）
            </label>
            <textarea
              value={project.concept ?? ''}
              onChange={(e) => onMetaChange({ concept: e.target.value })}
              rows={4}
              maxLength={5000}
              placeholder="例: 新発売のオーガニックコーヒー豆のプロモーション動画。朝の静かなキッチンで丁寧にコーヒーを淹れる時間の豊かさを伝え、最後に商品パッケージを印象的に見せる。"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-3"
            />
          </>
        )}

        <label className="block text-xs text-gray-600 mb-1">
          全体スタイル（画風・色調・トーン。全シーンの絵コンテに共通で適用）
        </label>
        <textarea
          value={project.style_description ?? ''}
          onChange={(e) => onMetaChange({ style_description: e.target.value })}
          rows={2}
          maxLength={3000}
          placeholder="例: シネマティックな実写風。暖色系の朝の光、浅い被写界深度、フィルムグレイン。"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary mb-3"
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">アスペクト比</label>
            <TaskySelect
              value={project.aspect_ratio}
              onChange={(e) => onMetaChange({ aspect_ratio: e.target.value })}
              disabled={hasGeneratedAssets}
              className="h-8 text-sm"
            >
              <option value="16:9">16:9（横長）</option>
              <option value="9:16">9:16（縦長）</option>
              <option value="1:1">1:1（正方形）</option>
            </TaskySelect>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">目標尺</label>
            <TaskySelect
              value={project.target_duration_sec}
              onChange={(e) => onMetaChange({ target_duration_sec: Number(e.target.value) })}
              className="h-8 text-sm"
            >
              {[15, 30, 45, 60, 90, 120].map((sec) => (
                <option key={sec} value={sec}>{sec}秒</option>
              ))}
            </TaskySelect>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">動画モデル</label>
            <button
              type="button"
              aria-label={`動画モデルを選択: ${modelSpec.label}`}
              onClick={() => setShowModelModal(true)}
              disabled={isSavingMeta || isGeneratingScript}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-left text-sm bg-white hover:border-violet-500 disabled:opacity-50"
            >
              <span className="block">{modelSpec.label}</span>
              <span className="text-xs text-violet-600">サンプルを見て変更</span>
            </button>
          </div>
        </div>

        {project.video_model.startsWith('fal-') && (
          <p className="text-xs text-gray-600 mt-2">
            H3 Max / Turboは音声付きで生成します。
          </p>
        )}

        {hasGeneratedAssets && (
          <p className="text-xs text-amber-600 mt-2">
            ※ 生成済みの絵コンテ/クリップがあるため、アスペクト比は変更できません
          </p>
        )}

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={onSaveMeta}
            disabled={isSavingMeta}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" />
            {isSavingMeta ? '保存中...' : '設定を保存'}
          </button>
          <button
            onClick={handleGenerateClick}
            disabled={
              isGeneratingScript ||
              (inputMode === 'product' ? !selectedProductData : !(project.concept ?? '').trim())
            }
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {isGeneratingScript ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {isGeneratingScript ? 'シーン構成を生成中...' : 'AIでシーン構成を生成'}
          </button>
        </div>

        {confirmRegenerate && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            既存の{scenes.length}シーン（絵コンテ・クリップとの紐付け含む）は破棄されます。再生成しますか？
            <div className="flex gap-2 mt-2">
              <button
                onClick={handleGenerateClick}
                className="px-3 py-1 bg-amber-600 text-white text-xs rounded hover:bg-amber-700"
              >
                破棄して再生成
              </button>
              <button
                onClick={() => setConfirmRegenerate(false)}
                className="px-3 py-1 border border-amber-300 text-xs rounded hover:bg-amber-100"
              >
                キャンセル
              </button>
            </div>
          </div>
        )}
      </div>

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      <VideoModelSelectModal
        isOpen={showModelModal}
        currentModel={project.video_model}
        allowedModels={MOVIE_VIDEO_MODELS}
        onSelect={(model) => onMetaChange({ video_model: model as MovieVideoModel })}
        onClose={() => setShowModelModal(false)}
      />

      {/* シーン一覧 */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">
            シーン構成
            {scenes.length > 0 && (
              <span className="ml-2 text-xs font-normal text-gray-500">
                {scenes.length}シーン / 合計約{totalDuration}秒
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={addScene}
              disabled={scenes.length >= 10}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" />
              シーン追加
            </button>
            <button
              onClick={onSaveScenes}
              disabled={isSavingScenes || scenes.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              <Save className="w-3.5 h-3.5" />
              {isSavingScenes ? '保存中...' : 'シーンを保存'}
            </button>
          </div>
        </div>

        {scenes.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm border border-dashed border-gray-300 rounded-xl">
            コンセプトを入力して「AIでシーン構成を生成」するか、「シーン追加」で手動作成してください
          </div>
        ) : (
          <div className="space-y-3">
            {scenes.map((scene, index) => (
              <div key={scene.key} className="border border-warm-border rounded-xl p-4 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">シーン {index + 1}</span>
                  <div className="flex items-center gap-1">
                    <TaskySelect
                      value={scene.duration_sec}
                      onChange={(e) => updateScene(index, { duration_sec: Number(e.target.value) })}
                      className="h-8 text-sm"
                    >
                      {DURATION_OPTIONS.map((sec) => (
                        <option key={sec} value={sec}>{sec}秒</option>
                      ))}
                    </TaskySelect>
                    <button
                      onClick={() => moveScene(index, -1)}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      title="上へ"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => moveScene(index, 1)}
                      disabled={index === scenes.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                      title="下へ"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => removeScene(index)}
                      className="p-1 text-gray-400 hover:text-red-500"
                      title="削除"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <input
                  type="text"
                  value={scene.summary ?? ''}
                  onChange={(e) => updateScene(index, { summary: e.target.value })}
                  placeholder="シーンの要約（例: 朝日が差し込むキッチンでコーヒーを淹れる手元）"
                  maxLength={100}
                  className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mb-2 focus:outline-none focus:ring-1 focus:ring-primary"
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">絵コンテプロンプト（日本語・静止画）</label>
                    <textarea
                      value={scene.storyboard_prompt ?? ''}
                      onChange={(e) => updateScene(index, { storyboard_prompt: e.target.value })}
                      rows={3}
                      maxLength={5000}
                      className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-gray-500 mb-0.5">クリッププロンプト（英語・動き）</label>
                    <textarea
                      value={scene.clip_prompt ?? ''}
                      onChange={(e) => updateScene(index, { clip_prompt: e.target.value })}
                      rows={3}
                      maxLength={5000}
                      className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScriptStep;
